import { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

interface DropdownOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface DropdownSelectProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  className?: string;
}

export default function DropdownSelect({
  options,
  value,
  onChange,
  placeholder = '请选择',
  disabled = false,
  loading = false,
  icon,
  className = ''
}: DropdownSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        disabled={disabled || loading}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-border-default rounded-lg text-sm text-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-border-default"
      >
        {icon}
        <span className="max-w-[100px] truncate">
          {loading ? '加载中...' : (selectedOption?.label || placeholder)}
        </span>
        <ChevronDownIcon className="h-3 w-3 text-gray-500" />
      </button>
      {isOpen && options.length > 0 && (
        <div className="absolute bottom-full left-0 mb-2 bg-bg-surface border border-border-subtle rounded-lg shadow-lg overflow-hidden z-50 max-h-48 overflow-y-auto min-w-[180px]">
          {options.map(option => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-bg-elevated transition-colors flex items-center gap-2 ${value === option.value ? 'text-primary bg-bg-elevated' : 'text-gray-300'
                }`}
            >
              {option.icon}
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
